import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, HandHeart, Heart } from "@phosphor-icons/react";

export default function FeedbackSection() {
  const [form, setForm] = useState({ name: "", email: "", business_type: "restaurant", role: "", message: "", rating: 5 });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error("Tell us what's on your mind.");
    setBusy(true);
    try {
      await api.post("/feedback", form);
      setSent(true);
      toast.success("Thank you — we read every note.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
    setBusy(false);
  };

  return (
    <section id="feedback" className="px-6 pb-24">
      <div className="max-w-5xl mx-auto mb-8">
        <div className="paper-card p-8 bg-[#C2D1C1]/40 border-[#1E3A2F]/20 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <Heart size={22} color="#D96C5B" weight="fill" />
          </div>
          <div>
            <div className="overline mb-1">A promise to operators</div>
            <p className="text-[#1E3A2F] leading-relaxed">
              We know restaurants — and most hospitality businesses — operate on <b>razor-thin margins</b>. Every dollar you spend on software is a dollar not going to your team, your ingredients, or your rent. That's why we will always strive to keep BuzzerAI <b>affordable, useful, and honest</b>. No feature bloat, no surprise price hikes, no long contracts. If a line item on your invoice isn't paying for itself, cancel it — no questions asked.
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto paper-card p-10 grid md:grid-cols-2 gap-10">
        <div>
          <div className="w-12 h-12 rounded-lg bg-[#E6E2D6] flex items-center justify-center mb-4">
            <HandHeart size={24} color="#1E3A2F" />
          </div>
          <div className="overline mb-3">How can we improve?</div>
          <h2 className="text-3xl mb-4">This platform is built <em>with</em> operators, not just for them.</h2>
          <p className="text-[#5C6B61] leading-relaxed mb-4">
            BuzzerAI is designed and shaped by working restaurant and hospitality operators — people who've stood at a full host stand, taken a to-go order over a screaming line, and lost sleep over a missed call.
          </p>
          <p className="text-[#5C6B61] leading-relaxed">
            We want BuzzerAI to be as accessible and invaluable to your operation as possible. If a workflow feels clunky, if a feature is missing, or if the pricing doesn't fit — tell us. We ship changes weekly based on operator notes.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-[#5C6B61]">
            <div className="paper-card p-3"><b className="text-[#121914]">Built by operators</b><br />Not another software team pretending to know your Friday night</div>
            <div className="paper-card p-3"><b className="text-[#121914]">No contracts</b><br />Cancel any add-on the second it isn't paying for itself</div>
          </div>
        </div>

        {sent ? (
          <div className="flex flex-col justify-center paper-card p-8 bg-[#C2D1C1]/40 border-[#1E3A2F]/20 text-center">
            <div className="w-14 h-14 rounded-full bg-white mx-auto flex items-center justify-center mb-3">
              <PaperPlaneTilt size={22} color="#1E3A2F" weight="fill" />
            </div>
            <h3 className="text-2xl mb-2">Got it — thank you.</h3>
            <p className="text-[#5C6B61]">Every note lands in our product room. If you left contact info, we'll circle back within a week.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="feedback-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Your name</label>
                <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="feedback-name" />
              </div>
              <div>
                <label className="overline block mb-1">Role</label>
                <input className="input-field" placeholder="Owner, GM, Host…" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="feedback-role" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Email (optional)</label>
                <input type="email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="feedback-email" />
              </div>
              <div>
                <label className="overline block mb-1">Business type</label>
                <select className="input-field" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} data-testid="feedback-type">
                  <option value="restaurant">Restaurant</option>
                  <option value="salon">Salon</option>
                  <option value="clinic">Clinic</option>
                  <option value="retail">Retail</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="overline block mb-1">What would make BuzzerAI invaluable to you?</label>
              <textarea rows={5} required className="input-field" placeholder="A workflow that feels clunky, a missing feature, a pricing note — anything."
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="feedback-message" />
            </div>
            <div>
              <label className="overline block mb-2">How well does it fit your operation today?</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}
                    className={`w-10 h-10 rounded-full border transition-[background-color,transform] duration-200 ${form.rating === n ? "bg-[#1E3A2F] text-white border-[#1E3A2F] -translate-y-0.5" : "border-[#E1DFD5] text-[#121914] hover:bg-[#E6E2D6]"}`}
                    data-testid={`feedback-rating-${n}`}>{n}</button>
                ))}
                <span className="text-xs text-[#5C6B61] ml-2">1 = not yet · 5 = we can't live without it</span>
              </div>
            </div>
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="feedback-submit">
              {busy ? "Sending…" : "Send feedback"}
            </button>
            <p className="text-xs text-[#5C6B61] text-center">We read every note personally. No newsletter, no drip campaign.</p>
          </form>
        )}
      </div>
    </section>
  );
}
